<template>
    <div
        v-if="is404"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>
                    {{ $t('messages.batchContactsList.noBatchContact', { id: batchContactsListId }) }}
                </p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="batchContactsList.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-2">
                    <div class="form-group">
                        <label>{{ $t('active') }}</label>

                        <FormSwitcher
                            v-model="batchContactsList.active"
                            :disabled="isLoading"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-3">
                    <div class="form-group">
                        <label>{{ $t('batchContactsList.labels') }}</label>
                        <FormSelect
                            v-model="batchContactsList.labels"
                            :disabled="isLoading"
                            :loading="isLoadingLabels"
                            :options="labels"
                            :placeholder="`${$t('select')} ${$t('batchContactsList.labels')}`"
                            allowEmpty
                            label="name"
                            multiple
                            searchable
                            trackBy="id"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6  col-lg-3">
                    <template v-if="isUploading">
                        <label>{{ $t('uploading') }}</label>

                        <div
                            class="progress"
                            :class="{
                                'progress-primary': uploadingProgressValue < 100,
                                'progress-success': uploadingProgressValue >= 100,
                            }"
                        >
                            <div
                                class="progress-bar progress-bar-striped active"
                                :class="{
                                    'progress-bar-primary': uploadingProgressValue < 100,
                                    'progress-bar-success': uploadingProgressValue >= 100,
                                }"
                                role="progressbar"
                                :aria-valuenow="uploadingProgressValue"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                :style="{ width: `${uploadingProgressValue}%` }"
                            >
                                <div class="progress-bar-value">{{ uploadingProgressText }}</div>
                            </div>
                        </div>
                    </template>

                    <ValidationProvider
                        v-else
                        v-slot="{ errors, validate }"
                        name="Contacts List File"
                        vid="file"
                        :rules="!isEdit ? 'required|' : '' + rules"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>
                                {{ $t('batchContactsList.contactsListFile') }}
                                <b
                                    v-if="!isEdit"
                                    class="text-danger"
                                >
                                    *
                                </b>
                            </label>

                            <br />

                            <label
                                class="btn btn-primary"
                                :disabled="isUploading || isLoading"
                            >
                                {{ batchContactsList.fileName || $t('addFile') }}

                                <input
                                    ref="file"
                                    type="file"
                                    :accept="accept"
                                    :disabled="isUploading || isLoading"
                                    hidden
                                    @change="uploadFile($event, validate)"
                                />
                            </label>

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>

                    <p class="help-block">
                        * File format: CSV
                        <br />
                        * Column: phone number ids
                    </p>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-8">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isLoading || invalid"
                        @click="submitBatchContactsList"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isLoading"
                        @click="
                            $router.push({
                                name: 'lists.batchContacts.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import FormSwitcher from '@/components/form/FormSwitcher';
import FormSelect from '@/components/form/FormSelect';

export default {
    name: 'ListsBatchContactsForm',
    components: {
        FormSwitcher,
        FormSelect,
    },
    props: {
        batchContactsListId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            is404: false,
            isUploading: false,
            isLoading: false,
            isLoadingLabels: false,
            uploadingProgressValue: 0,
            uploadingProgressText: '',
            labels: [],
            batchContactsList: {
                name: '',
                active: true,
                file: '',
                labels: [],
            },
            rules: 'mimes:text/csv,text/plain,application/csv,text/comma-separated-values,application/octet-stream,application/txt',
            accept: 'text/csv,text/plain,application/csv,text/comma-separated-values,application/octet-stream,application/txt',
        };
    },
    computed: {
        isEdit() {
            return !!this.batchContactsListId;
        },
    },
    async created() {
        if (this.isEdit) {
            await this.fetchBatchContactsList();
        }

        await this.fetchLabels();

        this.$emit('loaded', this.batchContactsList);
    },
    methods: {
        async fetchBatchContactsList() {
            try {
                this.isLoading = true;

                const {
                    data: { batchContactsList = {} },
                } = await axios.get(`/lists/batch-contacts/data/${this.batchContactsListId}`);

                if (this.isEdit) {
                    batchContactsList.fileName = '';
                }

                this.batchContactsList = batchContactsList;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },

        async submitBatchContactsList() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            const requestData = new FormData();
            requestData.append('name', this.batchContactsList.name);
            requestData.append('active', Number(this.batchContactsList.active));
            this.isEdit && requestData.append('id', this.batchContactsList.id);

            for (let i = 0; i < this.batchContactsList.labels.length; i++) {
                requestData.append('labels[]', Number(this.batchContactsList.labels[i].id));
            }

            if (this.batchContactsList.file) {
                this.isUploading = true;
                requestData.append('file', this.batchContactsList.file);
            }

            this.isLoading = true;

            try {
                const {
                    data: { action },
                } = await axios({
                    method: 'POST',
                    url: `/lists/batch-contacts/data${this.isEdit ? `/${this.batchContactsListId}` : ''}`,
                    data: requestData,
                    onUploadProgress: !this.batchContactsList.file
                        ? undefined
                        : ({ loaded, total }) => {
                              this.uploadingProgressValue = Math.floor((loaded / total) * 100);

                              if (total < 1024) {
                                  this.uploadingProgressText = `${loaded} / ${total} Bytes`;
                              } else if (total < 1024 * 1024) {
                                  this.uploadingProgressText = `${(loaded / 1024).toFixed(2)} / ${(total / 1024).toFixed(2)} KB`;
                              } else {
                                  this.uploadingProgressText = `${(loaded / (1024 * 1024)).toFixed(2)} / ${(total / (1024 * 1024)).toFixed(
                                      2
                                  )} MB`;
                              }
                          },
                });

                if (action === 'created') {
                    this.onCreated(this.batchContactsList.name);
                } else if (action === 'updated') {
                    this.onUpdated(this.batchContactsList.id, this.batchContactsList.name);
                }
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observer.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
                this.isUploading = false;

                if (!this.$refs.file) {
                    this.batchContactsList.file = null;
                    this.batchContactsList.fileName = null;
                }
            }
        },

        uploadFile(event, validate) {
            if ((event && !event.target.files[0]) || !validate(event)) return;

            this.batchContactsList.file = event.target.files[0];
            this.batchContactsList.fileName = event.target.files[0].name;
        },

        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'lists.batchContacts.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.batchContactsList.updated', { name }),
            });
        },

        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'lists.batchContacts.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.batchContactsList.created', { name }),
            });
        },

        async fetchLabels() {
            try {
                this.isLoadingLabels = true;

                const {
                    data: { labels },
                } = await axios.get(`/campaigns/collect/labels`);

                this.batchContactsList.labels = this.batchContactsList.labels.filter((b) => {
                    return labels.find((v) => v.id === b.id);
                });

                this.labels = labels || [];
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingLabels = false;
            }
        },
    },
};
</script>

<style src="./ListsBatchContactsForm.css" />
